/**
 * Import Trades — uploads a Bajaj .xls trade-book, previews parsed fills and
 * aggregated trades, then commits to DynamoDB.
 *
 * Stays consistent with the rest of the app's neutral styling (off-white
 * surface, hairline borders, near-black accent, sober status colors).
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Chip,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  Snackbar,
  CircularProgress,
  TextField,
  Tooltip,
  IconButton,
  Tab,
  Tabs,
  Select,
  MenuItem,
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CloseIcon from '@mui/icons-material/Close';

import { importsService, ImportPreview, ImportCommitResult, AggregatedTrade, Classifications, TradeDecision } from '../services/imports.service';
import { useImportDrop } from '../contexts/ImportDropContext';

/** Key a disputed trade the same way the backend does. */
const tradeKey = (t: AggregatedTrade): string => `${t.clientId}|${t.scrip}|${t.exchange}`;

const formatINR = (n: number): string =>
  new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 2 }).format(n);

const formatQty = (n: number): string =>
  new Intl.NumberFormat('en-IN', { maximumFractionDigits: 4 }).format(n);

export function ImportTradesPage() {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [tradeDate, setTradeDate] = useState<string>(''); // YYYY-MM-DD, blank = auto-detect from filename
  const [preview, setPreview] = useState<ImportPreview | null>(null);
  const [loading, setLoading] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [tab, setTab] = useState<'aggregated' | 'fills'>('aggregated');
  // Per-disputed-trade decisions for SELL_ONLY trades. Absent ⇒ 'ignore' (default).
  const [classifications, setClassifications] = useState<Classifications>({});

  // Drag-and-drop visual state (zone-level)
  const [dragActive, setDragActive] = useState(false);

  // Files dropped on any page get routed here via context
  const { consumePendingFiles } = useImportDrop();

  const addFiles = useCallback((files: File[]) => {
    const accepted = files.filter((f) => /\.(xls|xlsx|csv)$/i.test(f.name));
    const rejected = files.filter((f) => !/\.(xls|xlsx|csv)$/i.test(f.name));
    if (rejected.length) {
      setError(`Skipped unsupported files: ${rejected.map((r) => r.name).join(', ')}`);
    }
    if (accepted.length === 0) return;
    setSelectedFiles((prev) => {
      // De-dupe by name+size to avoid accidentally listing the same file twice
      const seen = new Set(prev.map((p) => `${p.name}|${p.size}`));
      const next = [...prev];
      for (const f of accepted) {
        const k = `${f.name}|${f.size}`;
        if (!seen.has(k)) {
          next.push(f);
          seen.add(k);
        }
      }
      return next;
    });
    setPreview(null);
  }, []);

  // Auto-load files dropped from elsewhere in the app
  useEffect(() => {
    const dropped = consumePendingFiles();
    if (dropped.length) addFiles(dropped);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const removeFile = (idx: number) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== idx));
    setPreview(null);
  };

  const onFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length) addFiles(files);
    // Allow re-selecting the same file by clearing input value
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const onDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragActive(false);
    const files = Array.from(e.dataTransfer.files || []);
    if (files.length) addFiles(files);
  }, [addFiles]);

  const handlePreview = async () => {
    if (selectedFiles.length === 0) {
      setError('Please choose at least one file first.');
      return;
    }
    setLoading(true);
    setError(null);
    setPreview(null);
    setClassifications({});
    try {
      const result = await importsService.preview(selectedFiles, tradeDate || undefined);
      setPreview(result);
    } catch (e: any) {
      const apiErr = e?.response?.data?.error?.message || e?.message || 'Preview failed';
      setError(apiErr);
    } finally {
      setLoading(false);
    }
  };

  // Sell-only disputes default to SHORT (created) unless explicitly ignored.
  const shortsSelected = useMemo(() => {
    if (!preview) return 0;
    return preview.aggregatedTrades.filter(
      (t) =>
        t.status === 'needs-review' &&
        t.reviewCategory === 'SELL_ONLY' &&
        classifications[tradeKey(t)] !== 'ignore',
    ).length;
  }, [preview, classifications]);

  const handleCommit = async () => {
    if (selectedFiles.length === 0 || !preview) return;
    if (preview.summary.tradesToCreate + preview.summary.tradesToUpdate + shortsSelected === 0) {
      setError('Nothing to import — all rows are duplicates or need review.');
      return;
    }
    setCommitting(true);
    setError(null);
    try {
      const result = (await importsService.commit(selectedFiles, preview.tradeDate, classifications)) as ImportCommitResult;
      setPreview(result); // refresh with the commit result
      const c = result.committed;
      setSuccess(
        `Imported ${c.fillsWritten} fills · ${c.tradesCreated} trades created · ${c.tradesUpdated} updated · ${c.fillsSkipped} skipped`,
      );
    } catch (e: any) {
      const apiErr = e?.response?.data?.error?.message || e?.message || 'Commit failed';
      setError(apiErr);
    } finally {
      setCommitting(false);
    }
  };

  const handleReset = () => {
    setSelectedFiles([]);
    setPreview(null);
    setTradeDate('');
    setError(null);
    setSuccess(null);
    setClassifications({});
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // ── Memoized derived data for tables ─────────────────────────────────
  const aggregatedByClient = useMemo(() => {
    if (!preview) return new Map<string, { clientName: string; trades: AggregatedTrade[] }>();
    const m = new Map<string, { clientName: string; trades: AggregatedTrade[] }>();
    for (const t of preview.aggregatedTrades) {
      if (!m.has(t.clientId)) m.set(t.clientId, { clientName: t.clientName, trades: [] });
      m.get(t.clientId)!.trades.push(t);
    }
    return m;
  }, [preview]);

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1400, mx: 'auto' }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 600, color: '#0A0A0A', letterSpacing: '-0.01em' }}>
          Import Trades
        </Typography>
        <Typography variant="body2" sx={{ color: '#737373', mt: 0.5 }}>
          Upload a Bajaj trade-book Excel file. The system parses every fill, dedupes against past uploads, and
          aggregates per-client per-scrip into MIS trade records.
        </Typography>
      </Box>

      {/* ── Upload card ─────────────────────────────────────────────── */}
      <Paper
        elevation={0}
        sx={{
          p: 3,
          mb: 3,
          borderRadius: 2,
          bgcolor: '#FFFFFF',
          border: '1px solid #E5E5E5',
        }}
      >
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="stretch">
          <Box
            onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
            onDragLeave={() => setDragActive(false)}
            onDrop={onDrop}
            onClick={() => fileInputRef.current?.click()}
            sx={{
              flex: 1,
              border: '1.5px dashed',
              borderColor: dragActive ? '#0A0A0A' : '#D4D4D4',
              borderRadius: 2,
              bgcolor: dragActive ? '#FAFAFA' : '#FCFCFC',
              cursor: 'pointer',
              py: 2,
              px: 2,
              transition: 'all 0.15s',
              '&:hover': { borderColor: '#A3A3A3', bgcolor: '#FAFAFA' },
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <UploadFileIcon sx={{ color: '#737373', fontSize: 28 }} />
              <Box>
                <Typography variant="body2" sx={{ color: '#0A0A0A', fontWeight: 500 }}>
                  {selectedFiles.length > 0
                    ? `${selectedFiles.length} file${selectedFiles.length === 1 ? '' : 's'} selected`
                    : 'Drop .xls / .xlsx / .csv here or click to browse'}
                </Typography>
                <Typography variant="caption" sx={{ color: '#A3A3A3' }}>
                  {selectedFiles.length > 0
                    ? `Total ${(selectedFiles.reduce((s, f) => s + f.size, 0) / 1024).toFixed(1)} KB · click to add more`
                    : 'Bajaj trade-book files from broker portal · multiple files supported'}
                </Typography>
              </Box>
            </Box>

            {selectedFiles.length > 0 && (
              <Box
                onClick={(e) => e.stopPropagation()}
                sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}
              >
                {selectedFiles.map((f, i) => (
                  <Chip
                    key={`${f.name}-${i}`}
                    label={`${f.name}  ·  ${(f.size / 1024).toFixed(1)} KB`}
                    size="small"
                    onDelete={() => removeFile(i)}
                    deleteIcon={<CloseIcon sx={{ fontSize: 14 }} />}
                    sx={{
                      bgcolor: '#FFFFFF',
                      border: '1px solid #E5E5E5',
                      fontSize: '0.72rem',
                      color: '#0A0A0A',
                      height: 24,
                    }}
                  />
                ))}
              </Box>
            )}

            <input
              ref={fileInputRef}
              type="file"
              accept=".xls,.xlsx,.csv"
              multiple
              hidden
              onChange={onFileInputChange}
            />
          </Box>

          <Box sx={{ minWidth: 220 }}>
            <TextField
              label="Trade Date"
              type="date"
              size="small"
              fullWidth
              value={tradeDate}
              onChange={(e) => setTradeDate(e.target.value)}
              helperText="Optional — auto-detected from filename"
              InputLabelProps={{ shrink: true }}
              sx={{ '& .MuiOutlinedInput-root': { bgcolor: '#FAFBFC' } }}
            />
          </Box>

          <Stack direction="row" spacing={1} alignItems="flex-start">
            <Button
              variant="contained"
              disabled={selectedFiles.length === 0 || loading}
              onClick={handlePreview}
              startIcon={loading ? <CircularProgress size={16} sx={{ color: 'white' }} /> : null}
              sx={{
                bgcolor: '#0A0A0A',
                color: '#FFFFFF',
                textTransform: 'none',
                px: 2.5,
                py: 1,
                boxShadow: 'none',
                '&:hover': { bgcolor: '#262626', boxShadow: 'none' },
                '&.Mui-disabled': { bgcolor: '#E5E5E5', color: '#A3A3A3' },
              }}
            >
              {loading ? 'Parsing…' : selectedFiles.length > 1 ? `Preview (${selectedFiles.length})` : 'Preview'}
            </Button>
            {(selectedFiles.length > 0 || preview) && (
              <Tooltip title="Reset">
                <IconButton onClick={handleReset} size="small">
                  <RestartAltIcon />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        </Stack>
      </Paper>

      {/* ── Error / Success Banners ─────────────────────────────────── */}
      {error && (
        <Alert
          severity="error"
          icon={<ErrorOutlineIcon />}
          onClose={() => setError(null)}
          sx={{ mb: 2, bgcolor: '#FEF2F2', color: '#B91C1C', border: '1px solid #FCA5A5' }}
        >
          {error}
        </Alert>
      )}

      {/* ── Preview ────────────────────────────────────────────────── */}
      {preview && (
        <>
          <Paper elevation={0} sx={{ p: 2, mb: 2, borderRadius: 2, bgcolor: '#FFFFFF', border: '1px solid #E5E5E5' }}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems={{ md: 'center' }} useFlexGap flexWrap="wrap">
              <Typography variant="caption" sx={{ color: '#737373', fontWeight: 500, letterSpacing: '0.04em', textTransform: 'uppercase' }}>
                {preview.filename}  ·  {preview.tradeDate}  ·  {preview.totalRows} rows parsed
              </Typography>
              <Box sx={{ flex: 1 }} />
              <SummaryChip label="New fills" count={preview.summary.newFills} tone="positive" />
              <SummaryChip label="Duplicates" count={preview.summary.dupeFills} tone="muted" />
              <SummaryChip label="Trades to create" count={preview.summary.tradesToCreate} tone="positive" />
              <SummaryChip label="Trades to update" count={preview.summary.tradesToUpdate} tone="info" />
              {preview.summary.needsReview > 0 && (
                <SummaryChip label="Needs review" count={preview.summary.needsReview} tone="warning" />
              )}
              {preview.summary.unmappedUserIds.length > 0 && (
                <SummaryChip label="Unmapped clients" count={preview.summary.unmappedUserIds.length} tone="negative" />
              )}
            </Stack>
          </Paper>

          {/* Unmapped panel */}
          {preview.summary.unmappedUserIds.length > 0 && (
            <Alert
              severity="warning"
              icon={<WarningAmberIcon />}
              sx={{ mb: 2, bgcolor: '#FFFBEB', color: '#92400E', border: '1px solid #FCD34D' }}
            >
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                Unmapped broker client codes — these rows will be skipped:
              </Typography>
              <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                {preview.summary.unmappedUserIds.map((u) => (
                  <Chip
                    key={u}
                    label={u}
                    size="small"
                    sx={{
                      bgcolor: 'transparent',
                      border: '1px solid #FCD34D',
                      color: '#92400E',
                      fontWeight: 500,
                      fontSize: '0.7rem',
                    }}
                  />
                ))}
              </Stack>
              <Typography variant="caption" sx={{ display: 'block', mt: 1, color: '#92400E' }}>
                Add these codes via Clients → Edit (or create new clients) and re-upload.
              </Typography>
            </Alert>
          )}

          {/* Parser errors */}
          {preview.errors.length > 0 && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                Parser warnings ({preview.errors.length}):
              </Typography>
              <Box component="ul" sx={{ m: 0, pl: 2 }}>
                {preview.errors.slice(0, 10).map((e, i) => (
                  <li key={i}>
                    <Typography variant="caption" sx={{ color: '#92400E' }}>{e}</Typography>
                  </li>
                ))}
                {preview.errors.length > 10 && (
                  <li>
                    <Typography variant="caption" sx={{ color: '#92400E' }}>… and {preview.errors.length - 10} more</Typography>
                  </li>
                )}
              </Box>
            </Alert>
          )}

          {/* Tabs: Aggregated trades vs raw fills */}
          <Paper elevation={0} sx={{ borderRadius: 2, bgcolor: '#FFFFFF', border: '1px solid #E5E5E5' }}>
            <Tabs
              value={tab}
              onChange={(_, v) => setTab(v)}
              sx={{
                borderBottom: '1px solid #E5E5E5',
                minHeight: 40,
                '& .MuiTab-root': { textTransform: 'none', fontWeight: 500, fontSize: '0.85rem', minHeight: 40, color: '#737373' },
                '& .Mui-selected': { color: '#0A0A0A' },
                '& .MuiTabs-indicator': { bgcolor: '#0A0A0A' },
              }}
            >
              <Tab label={`Aggregated Trades (${preview.aggregatedTrades.length})`} value="aggregated" />
              <Tab label={`Raw Fills (${preview.parsedFills.length})`} value="fills" />
            </Tabs>

            {tab === 'aggregated' && (
              <Box>
                {aggregatedByClient.size === 0 ? (
                  <Box sx={{ p: 4, textAlign: 'center', color: '#A3A3A3' }}>
                    <Typography variant="body2">No trades to import.</Typography>
                  </Box>
                ) : (
                  Array.from(aggregatedByClient.entries()).map(([clientId, { clientName, trades }]) => (
                    <Box key={clientId} sx={{ borderBottom: '1px solid #F4F4F5' }}>
                      <Box sx={{ px: 2, py: 1, bgcolor: '#FAFAFA', display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#0A0A0A' }}>
                          {clientName}
                        </Typography>
                        <Chip
                          label={`${trades.length} ${trades.length === 1 ? 'trade' : 'trades'}`}
                          size="small"
                          sx={{ height: 18, fontSize: '0.65rem', bgcolor: '#F4F4F5', color: '#525252', border: '1px solid #E5E5E5' }}
                        />
                      </Box>
                      <TableContainer>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              {['Scrip', 'Type', 'Exch', 'Buy Qty', 'Buy ₹', 'Sell Qty', 'Sell ₹', 'Brokerage', 'Status', 'Decision'].map((h) => (
                                <TableCell
                                  key={h}
                                  sx={{
                                    fontSize: '0.65rem',
                                    fontWeight: 500,
                                    color: '#737373',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.04em',
                                    bgcolor: '#FFFFFF',
                                  }}
                                  align={['Buy Qty', 'Buy ₹', 'Sell Qty', 'Sell ₹', 'Brokerage'].includes(h) ? 'right' : 'left'}
                                >
                                  {h}
                                </TableCell>
                              ))}
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {trades.map((t, i) => (
                              <TableRow
                                key={`${t.scrip}-${i}`}
                                sx={{
                                  bgcolor: t.status === 'needs-review' ? '#FFFBEB' : 'transparent',
                                  '&:hover': { bgcolor: t.status === 'needs-review' ? '#FEF3C7' : '#FAFAFA' },
                                }}
                              >
                                <TableCell sx={{ fontSize: '0.8rem', fontWeight: 500, color: '#0A0A0A' }}>{t.scrip}</TableCell>
                                <TableCell sx={{ fontSize: '0.75rem', color: '#525252' }}>{t.tradeType}</TableCell>
                                <TableCell sx={{ fontSize: '0.75rem', color: '#737373' }}>{t.exchange}</TableCell>
                                <TableCell align="right" sx={{ fontSize: '0.78rem', color: '#0A0A0A', fontVariantNumeric: 'tabular-nums' }}>
                                  {formatQty(t.purchaseLots * t.lotSize)}
                                </TableCell>
                                <TableCell align="right" sx={{ fontSize: '0.78rem', color: '#0A0A0A', fontVariantNumeric: 'tabular-nums' }}>
                                  {t.purchasePrice > 0 ? formatINR(t.purchasePrice) : '—'}
                                </TableCell>
                                <TableCell align="right" sx={{ fontSize: '0.78rem', color: '#0A0A0A', fontVariantNumeric: 'tabular-nums' }}>
                                  {t.saleLots > 0 ? formatQty(t.saleLots * t.lotSize) : '—'}
                                </TableCell>
                                <TableCell align="right" sx={{ fontSize: '0.78rem', color: '#0A0A0A', fontVariantNumeric: 'tabular-nums' }}>
                                  {t.saleLots > 0 ? formatINR(t.salePrice) : '—'}
                                </TableCell>
                                <TableCell align="right" sx={{ fontSize: '0.78rem', color: '#525252', fontVariantNumeric: 'tabular-nums' }}>
                                  {t.brokerageAmount != null ? formatINR(t.brokerageAmount) : '—'}
                                </TableCell>
                                <TableCell>
                                  <StatusBadge status={t.status} reason={t.reviewReason} />
                                </TableCell>
                                <TableCell>
                                  {t.status === 'needs-review' && t.reviewCategory === 'SELL_ONLY' ? (
                                    <Select
                                      size="small"
                                      value={classifications[tradeKey(t)] || 'short'}
                                      onChange={(e) =>
                                        setClassifications((prev) => ({
                                          ...prev,
                                          [tradeKey(t)]: e.target.value as TradeDecision,
                                        }))
                                      }
                                      sx={{
                                        fontSize: '0.72rem',
                                        height: 30,
                                        minWidth: 140,
                                        bgcolor: '#FFFFFF',
                                        '& .MuiSelect-select': { py: 0.4 },
                                      }}
                                    >
                                      <MenuItem value="short" sx={{ fontSize: '0.72rem' }}>
                                        Keep — short (sold to open)
                                      </MenuItem>
                                      <MenuItem value="ignore" sx={{ fontSize: '0.72rem' }}>
                                        Ignore (buy not in system)
                                      </MenuItem>
                                    </Select>
                                  ) : (
                                    <Typography sx={{ fontSize: '0.72rem', color: '#A3A3A3' }}>—</Typography>
                                  )}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Box>
                  ))
                )}
              </Box>
            )}

            {tab === 'fills' && (
              <TableContainer sx={{ maxHeight: 600 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      {['Row', 'Exch', 'Client', 'Side', 'Symbol', 'Qty', 'Price', 'Order #', 'Status'].map((h) => (
                        <TableCell
                          key={h}
                          sx={{
                            fontSize: '0.65rem',
                            fontWeight: 500,
                            color: '#737373',
                            textTransform: 'uppercase',
                            letterSpacing: '0.04em',
                            bgcolor: '#FAFAFA',
                          }}
                          align={['Qty', 'Price'].includes(h) ? 'right' : 'left'}
                        >
                          {h}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {preview.parsedFills.slice(0, 1000).map((f) => (
                      <TableRow
                        key={f.fillHash}
                        sx={{
                          bgcolor:
                            f.status === 'duplicate' ? '#FAFAFA' :
                            f.status === 'unmapped' ? '#FFFBEB' : 'transparent',
                          opacity: f.status === 'duplicate' ? 0.6 : 1,
                          '&:hover': { bgcolor: '#F4F4F5' },
                        }}
                      >
                        <TableCell sx={{ fontSize: '0.7rem', color: '#A3A3A3' }}>{f.rowIndex}</TableCell>
                        <TableCell sx={{ fontSize: '0.7rem', color: '#737373' }}>{f.exchSeg}</TableCell>
                        <TableCell sx={{ fontSize: '0.78rem', color: '#0A0A0A' }}>
                          {f.clientName || <Typography component="span" sx={{ color: '#B45309', fontSize: '0.78rem' }}>{f.userId}</Typography>}
                        </TableCell>
                        <TableCell sx={{ fontSize: '0.7rem' }}>
                          <Chip
                            label={f.side}
                            size="small"
                            sx={{
                              height: 18,
                              fontSize: '0.62rem',
                              fontWeight: 500,
                              bgcolor: 'transparent',
                              color: f.side === 'BUY' ? '#15803D' : '#B91C1C',
                              border: `1px solid ${f.side === 'BUY' ? '#86EFAC' : '#FCA5A5'}`,
                              '& .MuiChip-label': { px: 0.6 },
                            }}
                          />
                        </TableCell>
                        <TableCell sx={{ fontSize: '0.75rem', color: '#0A0A0A' }}>{f.tradingSymbol}</TableCell>
                        <TableCell align="right" sx={{ fontSize: '0.75rem', fontVariantNumeric: 'tabular-nums' }}>{formatQty(f.tradeQty)}</TableCell>
                        <TableCell align="right" sx={{ fontSize: '0.75rem', fontVariantNumeric: 'tabular-nums' }}>{formatINR(f.tradePrice)}</TableCell>
                        <TableCell sx={{ fontSize: '0.65rem', color: '#A3A3A3', fontFamily: 'monospace' }}>{f.exchangeOrderNo.slice(-6)}</TableCell>
                        <TableCell>
                          <StatusBadge status={f.status} reason={f.dupeReason} />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                {preview.parsedFills.length > 1000 && (
                  <Box sx={{ p: 1.5, textAlign: 'center', color: '#A3A3A3' }}>
                    <Typography variant="caption">
                      Showing first 1000 of {preview.parsedFills.length} rows. The full list is processed on commit.
                    </Typography>
                  </Box>
                )}
              </TableContainer>
            )}
          </Paper>

          {/* Commit action */}
          {preview.summary.needsReview > 0 && (
            <Alert
              severity="info"
              icon={<WarningAmberIcon fontSize="small" />}
              sx={{ mt: 2, bgcolor: '#FFFBEB', color: '#92400E', border: '1px solid #FDE68A', '& .MuiAlert-icon': { color: '#D97706' } }}
            >
              {preview.summary.needsReview} trade(s) need review. <strong>Sell-only</strong> rows are kept as <strong>shorts (sold to open) by default</strong>. Switch any to <strong>Ignore</strong> in the <strong>Decision</strong> column where the buy predates the system.
              {shortsSelected > 0 && ` — ${shortsSelected} will be created as shorts.`}
            </Alert>
          )}
          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end', gap: 1, alignItems: 'center' }}>
            {(() => {
              const toWrite = preview.summary.tradesToCreate + preview.summary.tradesToUpdate + shortsSelected;
              return toWrite > 0 ? (
                <Typography variant="caption" sx={{ color: '#525252' }}>
                  {toWrite} {toWrite === 1 ? 'trade' : 'trades'} will be written
                  {shortsSelected > 0 ? ` (incl. ${shortsSelected} short)` : ''}
                </Typography>
              ) : (
                <Typography variant="caption" sx={{ color: '#A3A3A3' }}>
                  Nothing to import — all rows are duplicates, unmapped, or need review.
                </Typography>
              );
            })()}
            <Button
              variant="contained"
              onClick={handleCommit}
              disabled={committing || (preview.summary.tradesToCreate + preview.summary.tradesToUpdate + shortsSelected === 0)}
              startIcon={committing ? <CircularProgress size={16} sx={{ color: 'white' }} /> : <CheckCircleOutlineIcon />}
              sx={{
                bgcolor: '#0A0A0A',
                color: '#FFFFFF',
                textTransform: 'none',
                px: 2.5,
                py: 1,
                boxShadow: 'none',
                '&:hover': { bgcolor: '#262626', boxShadow: 'none' },
                '&.Mui-disabled': { bgcolor: '#E5E5E5', color: '#A3A3A3' },
              }}
            >
              {committing ? 'Importing…' : `Import ${preview.summary.tradesToCreate + preview.summary.tradesToUpdate + shortsSelected} trades`}
            </Button>
          </Box>
        </>
      )}

      <Snackbar
        open={!!success}
        autoHideDuration={6000}
        onClose={() => setSuccess(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          severity="success"
          onClose={() => setSuccess(null)}
          sx={{ bgcolor: '#0A0A0A', color: '#FFFFFF', '& .MuiAlert-icon': { color: '#22C55E' } }}
        >
          {success}
        </Alert>
      </Snackbar>
    </Box>
  );
}

// ─── Helpers ───────────────────────────────────────────────────────────

function SummaryChip({
  label, count, tone,
}: { label: string; count: number; tone: 'positive' | 'negative' | 'warning' | 'info' | 'muted' }) {
  const colors = {
    positive: { bg: '#FFFFFF', text: '#15803D', border: '#86EFAC' },
    negative: { bg: '#FFFFFF', text: '#B91C1C', border: '#FCA5A5' },
    warning:  { bg: '#FFFFFF', text: '#B45309', border: '#FCD34D' },
    info:     { bg: '#FFFFFF', text: '#1D4ED8', border: '#93C5FD' },
    muted:    { bg: '#FAFAFA', text: '#525252', border: '#E5E5E5' },
  }[tone];
  return (
    <Chip
      label={
        <span>
          <span style={{ color: '#737373', marginRight: 4 }}>{label}</span>
          <strong>{count}</strong>
        </span>
      }
      size="small"
      sx={{
        height: 22,
        fontSize: '0.72rem',
        fontWeight: 500,
        bgcolor: colors.bg,
        color: colors.text,
        border: `1px solid ${colors.border}`,
        '& .MuiChip-label': { px: 1 },
      }}
    />
  );
}

function StatusBadge({ status, reason }: { status: string; reason?: string }) {
  const map: Record<string, { label: string; color: string; bg: string; border: string }> = {
    'new':         { label: 'New',         color: '#15803D', bg: '#FFFFFF', border: '#86EFAC' },
    'duplicate':   { label: 'Duplicate',   color: '#525252', bg: '#FAFAFA', border: '#E5E5E5' },
    'unmapped':    { label: 'Unmapped',    color: '#B45309', bg: '#FFFFFF', border: '#FCD34D' },
    'create':      { label: 'Create',      color: '#15803D', bg: '#FFFFFF', border: '#86EFAC' },
    'update':      { label: 'Update',      color: '#1D4ED8', bg: '#FFFFFF', border: '#93C5FD' },
    'needs-review':{ label: 'Review',      color: '#B45309', bg: '#FFFFFF', border: '#FCD34D' },
  };
  const cfg = map[status] || map['new'];
  const chip = (
    <Chip
      label={cfg.label}
      size="small"
      sx={{
        height: 18,
        fontSize: '0.62rem',
        fontWeight: 500,
        bgcolor: cfg.bg,
        color: cfg.color,
        border: `1px solid ${cfg.border}`,
        '& .MuiChip-label': { px: 0.6 },
      }}
    />
  );
  if (reason) {
    return (
      <Tooltip title={reason}>
        <span>{chip}</span>
      </Tooltip>
    );
  }
  return chip;
}

export default ImportTradesPage;
